<script>
import countTo from "vue-count-to";
import Monitor from "@/services/Monitor";

/**
 * Monitor Counters component
 */
export default {
  components: {
    countTo,
  },
  data() {
    return {
      deviceStats: null,
      userStats: null,
      cronStats: {
        data: [],
        date_from: null,
        date_to: null
      },
      user_count: 0,
      loaderTotalUsers: false,
      loaderTotalDevices: false,
      loaderQueueJobs: false,
      loaderCronStats: false,
      queuesData: {},
      error: ''
    };
  },
  mounted() {
    this.userStatPerTenant();
    this.getQueuesStats();
    this.deviceStatPerTenant();
    this.cronStatPerTenant();
  },
  computed:{
    iosTotal() {
      return this.getTotalCount('all_devices', 'IOS');
    },
    androidTotal() {
      return this.getTotalCount('all_devices', 'ANDROID');
    },
    totalDevices() {
      return this.iosTotal + this.androidTotal;
    },
    totalsUsers() {
      if (!this.userStats || !this.userStats.total || !this.userStats.total.users_by_gender) {
        return {
          total: 0,
          male: 0,
          female: 0,
          unknown: 0
        };
      }

      const totals = {
        total: this.userStats.total.total_users || 0,
        male: 0,
        female: 0,
        unknown: 0
      };

      this.userStats.total.users_by_gender.forEach(row => {
        if (row.gender === 'M') totals.male = row.total;
        else if (row.gender === 'F') totals.female = row.total;
        else totals.unknown += row.total;
      });

      return totals;
    },
    iosPercentage() {
      return this.calculatePercentage(this.iosTotal, this.androidTotal);
    },
    androidPercentage() {
      return this.calculatePercentage(this.androidTotal, this.iosTotal);
    },

  },
  methods: {
    async deviceStatPerTenant() {
      this.loaderTotalDevices = true;
      try {
        const response = await Monitor.deviceStatPerTenant();
        this.deviceStats = response.data;
      } catch (error) {
        console.error("Error fetching device stats:", error);
      } finally {
        this.loaderTotalDevices = false;
      }
    },

    getTotalCount(type, platform) {
      if (!this.deviceStats || !this.deviceStats.TOTAL) return 0;
      return this.getPlatformCount(this.deviceStats.TOTAL[type], platform);
    },

    getPlatformCount(devices, platform) {
      if (!devices) return 0;
      const device = devices.find(d => d.device_platform === platform);
      return device ? device.total : 0;
    },

    calculatePercentage(count, otherCount) {
      const total = count + otherCount;
      if (total === 0) return 0;
      return Math.round((count / total) * 100);
    },

    async getQueuesStats() {
      try {
        this.loaderQueueJobs = true
        const response = await Monitor.getQueuesStats();
        this.queuesData = response.data.data;
      } catch (error) {
        console.error('Error in getQueuesStats:', error);
        this.error = error.response?.data?.error || '';
      } finally {
        this.loaderQueueJobs = false
      }
    },

    async userStatPerTenant() {
      this.loaderTotalUsers = true;
      try {
        const response = await Monitor.userStatPerTenant();
        this.userStats = response.data;
      } catch (error) {
        console.error("Error fetching user stats:", error);
      } finally {
        this.loaderTotalUsers = false;
      }
    },

    async cronStatPerTenant() {
      this.loaderCronStats = true;
      try {
        const response = await Monitor.cronStatPerTenant();
        const { data, date_from, date_to } = response.data.data;
        this.cronStats = {
          data: data.filter(item => item.tenant?.toLowerCase() !== 'stvv'),
          date_from,
          date_to
        };
      } catch (error) {
        console.error("Error fetching cron stats:", error);
      } finally {
        this.loaderCronStats = false;
      }
    },

  },
};
</script>

<template>
  <div class="row">

    <div class="col-md-6 col-xl-3" style="margin-bottom: 0;" >
      <div class="card">
        <div class="card-body elementPointed" @click="goToURL('monitor/customer_stats')">
          <div class="d-flex justify-content-between align-items-center mb-3">
            <span class="text-muted" style="font-size: 1rem;">Total Users</span>
            <div
                class="d-flex justify-content-center align-items-center"
                style="width: 36px; height: 36px; border-radius: 12px; background-color: #e6f0ff;"
            >
              <i class="uil-users-alt" style="color: #3b82f6; font-size: 1.2rem;"></i>
            </div>
          </div>
          <div v-if="!loaderTotalUsers">
            <div class="mb-1">
              <h4 class="mb-0" style="font-weight: 400;">
                <span data-plugin="counterup">
                  <countTo
                      :startVal="0"
                      :endVal="totalsUsers.total"
                      :duration="2000"
                  ></countTo>
                </span>
                <span> Users</span>
              </h4>
            </div>
            <p class="mt-2 mb-0" style="font-size: 0.9rem;"><i class="fas fa-male me-1"></i> Male: {{ formatNumber(totalsUsers.male) }}</p>
            <p class="mt-2 mb-0" style="font-size: 0.9rem;"><i class="fas fa-female me-1"></i> Female: {{ formatNumber(totalsUsers.female) }}</p>
          </div>
          <div v-else class="d-flex justify-content-center">
            <b-spinner large></b-spinner>
          </div>

        </div>
      </div>
    </div>

    <div class="col-md-6 col-xl-3" style="margin-bottom: 0;" >
      <div class="card">
        <div class="card-body elementPointed" @click="goToURL('monitor/device_stats')">
          <div class="d-flex justify-content-between align-items-center mb-3">
            <span class="text-muted" style="font-size: 1rem;">Total Devices</span>
            <div
                class="d-flex justify-content-center align-items-center"
                style="width: 36px; height: 36px; border-radius: 12px; background-color: #e6f0ff;"
            >
              <i class="mdi mdi-devices" style="color: #3b82f6; font-size: 1.2rem;"></i>
            </div>
          </div>
          <div v-if="!loaderTotalDevices">
            <div class="mb-1">
              <h4 class="mb-0" style="font-weight: 400;">
                <span data-plugin="counterup">
                  <countTo
                      :startVal="0"
                      :endVal="totalDevices"
                      :duration="2000"
                  ></countTo>
                </span>
                <span> Devices</span>
              </h4>
            </div>
            <p class="mt-2 mb-0" style="font-size: 0.9rem;"><i class="fas fa-mobile-alt me-1"></i> iOS: {{ formatNumber(iosTotal) }} ({{ iosPercentage }}%)</p>
            <p class="mt-2 mb-0" style="font-size: 0.9rem;"><i class="fas fa-mobile me-1"></i> Android: {{ formatNumber(androidTotal) }} ({{ androidPercentage }}%)</p>
          </div>
          <div v-else class="d-flex justify-content-center">
            <b-spinner large></b-spinner>
          </div>

        </div>
      </div>
    </div>

    <div class="col-md-6 col-xl-3" style="margin-bottom: 0;" >
      <div class="card">
        <div class="card-body elementPointed" @click="goToURL('monitor/queues')">
          <div class="d-flex justify-content-between align-items-center mb-3">
            <span class="text-muted" style="font-size: 1rem;">Jobs in Queue</span>
            <div
                class="d-flex justify-content-center align-items-center"
                style="width: 36px; height: 36px; border-radius: 12px; background-color: #e6f0ff;"
            >
              <i class="mdi mdi-view-week" style="color: #3b82f6; font-size: 1.2rem;"></i>
            </div>
          </div>
          <div v-if="!loaderQueueJobs">
            <div v-for="(item) in queuesData" :key="item.id">
              <div class="mb-1">
                <h4 class="mb-0" style="font-weight: 400;">
                <span data-plugin="counterup">
                  <countTo
                      :startVal="0"
                      :endVal="item.cntQueues"
                      :duration="2000"
                  ></countTo>
                </span>
                  <span>Queue Jobs</span>
                </h4>
              </div>
              <p class="mt-2 mb-0 text-success" style="font-size: 0.9rem;"><i class="mdi mdi-check-outline me-1"></i> {{ formatNumber(item.cntSuccess) }} ({{item.lastSuccessAt}})</p>
              <p class="mt-2 mb-0 text-danger" style="font-size: 0.9rem;"><i class="mdi mdi-close-outline me-1"></i> {{ formatNumber(item.cntFailed) }} ({{item.lastFailedAt}})</p>
            </div>
          </div>
          <div v-else class="d-flex justify-content-center">
            <b-spinner large></b-spinner>
          </div>

        </div>
      </div>
    </div>

    <div class="col-9">
      <div class="card" >
        <div class="card-body elementPointed" v-if="!loaderCronStats">
          <p>Cron Script with status different than SUCCESS, period from: {{cronStats.date_from}} to {{cronStats.date_to}}</p>
          <div class="table-scroll">
            <table class="table table-responsive mt-3">
              <thead>
              <tr>
                <th>Cron</th>
                <th>Tenant</th>
                <th>Failed Count</th>
                <th>Total Count</th>
                <th>Failures (%)</th>
              </tr>
              </thead>
              <tbody>
              <tr v-for="(stat, index) in cronStats.data" :key="index">
                <td>{{ stat.cron }}</td>
                <td>{{ stat.tenant }}</td>
                <td>{{ stat.failed_count }}</td>
                <td>{{ stat.total_count }}</td>
                <td>
                  {{ stat.total_count > 0
                    ? ((stat.failed_count / stat.total_count) * 100).toFixed(2)
                    : 0
                  }}%
                </td>
              </tr>

              <tr v-if="!cronStats.data || cronStats.data.length === 0">
                <td colspan="5" class="text-center text-muted">
                  No data available
                </td>
              </tr>
              </tbody>
            </table>
          </div>
        </div>
        <div v-else class="d-flex justify-content-center m-5">
          <b-spinner large></b-spinner>
        </div>
      </div>

    </div>

  </div>
</template>
<style>
.table-scroll {
  max-height: 400px;   /* choose your height */
  overflow-y: auto;
}

.table-scroll thead th {
  position: sticky;
  top: 0;
  z-index: 2;
  background-color: #fff; /* REQUIRED */
}
</style>